// The pay period's words and its choice at the top of Hours and a Timesheet (PageHead's line and actions;
// PeriodHead folded into PageHead, BL-189). Until there's a pay period (QA-757) the pay line is one quiet
// line saying periods and locking come with payroll (PayrollLine), and the period's choice is ‹ This
// week › through payroll's weeks (WeekStep).
import type { ReactNode } from 'react';
import { Link } from '../components/Link/Link.tsx';
import { Segmented } from '../components/Segmented/Segmented.tsx';
import type { FigureItem } from '../components/Figures/Figures.tsx';
import type { NoPayrollLine } from './timesheet-view.ts';

/** The period's facts as the title block's figures: each once, a count above 0 a problem. */
export const periodFigures = (
  facts: readonly { label: string; value: string; tone?: 'bad' }[],
): FigureItem[] =>
  facts.map((f) => ({ label: f.label, value: f.value, ...(f.tone === 'bad' ? { bad: true } : {}) }));

/** The pay line: "Payroll Fri Oct 30", who locked it and sent it, or PayrollLine before payroll is set up. */
export const payLineOf = (
  payLine: ReactNode,
  noPayroll: NoPayrollLine | null | undefined,
  onSetUp: (() => void) | undefined,
): ReactNode => (noPayroll ? <PayrollLine line={noPayroll} onSetUp={onSetUp} /> : payLine);

/** The quiet line before payroll is set up, its link for those who may set it up (Books › Payroll). */
export function PayrollLine({ line, onSetUp }: { line: NoPayrollLine; onSetUp?: (() => void) | undefined }) {
  return (
    <>
      {line.words}
      {line.link && onSetUp ? (
        <>
          {' '}
          <Link onPress={onSetUp}>{line.link}</Link>
        </>
      ) : null}
    </>
  );
}

export interface WeekStepProps {
  /** The week shown has today in it: there's no week after it yet. */
  isLatest: boolean;
  /** The week before (-1), this week (0), the week after (1). */
  onStep: (dir: -1 | 0 | 1) => void;
}

/** ‹ This week ›: payroll's weeks until there's a pay period, stepped as the board steps its days. */
export function WeekStep({ isLatest, onStep }: WeekStepProps) {
  return (
    <Segmented
      label="Week"
      selectionMode="none"
      items={[
        { id: 'earlier', label: 'The week before', icon: 'chevron-left' },
        { id: 'this', label: 'This week' },
        { id: 'later', label: 'The week after', icon: 'chevron-right', isDisabled: isLatest },
      ]}
      {...(isLatest ? { on: 'this' } : {})}
      onAction={(id) => onStep(id === 'earlier' ? -1 : id === 'later' ? 1 : 0)}
    />
  );
}
